/**
 * Profile Menu Component
 *
 * Dropdown menu for user profile actions in the header.
 * Displays user avatar with initials and provides navigation
 * to profile editing and sign out functionality.
 *
 * @fileoverview Header profile dropdown menu component
 *
 * @example
 * <ProfileMenu />
 */

import React from 'react';
import {
  Box,
  HStack,
  Avatar,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Text,
  VStack,
  Icon,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { MdPerson, MdLogout } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../Context/userContext';

/**
 * Get user's initials from name or email
 *
 * @param user - User profile object
 * @returns Two-letter initials string
 */
function getUserInitials(user: {
  firstName?: string;
  lastName?: string;
  displayName?: string;
  email: string;
}): string {
  if (user.firstName && user.lastName) {
    return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
  }
  if (user.displayName) {
    const parts = user.displayName.trim().split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return user.displayName.substring(0, 2).toUpperCase();
  }
  return user.email.substring(0, 2).toUpperCase();
}

/**
 * Get user's display name
 *
 * @param user - User profile object
 * @returns Display name string
 */
function getUserDisplayName(user: {
  firstName?: string;
  lastName?: string;
  displayName?: string;
  email: string;
}): string {
  if (user.firstName && user.lastName) {
    return `${user.firstName} ${user.lastName}`;
  }
  if (user.displayName) {
    return user.displayName;
  }
  return user.email.split('@')[0];
}

/**
 * Profile Menu Component
 *
 * Renders a dropdown menu in the header with:
 * - User avatar with initials
 * - User name and email display
 * - Edit Profile navigation
 * - Sign Out action
 *
 * @returns Profile menu dropdown component
 *
 * @example
 * // In Header component
 * <Header
 *   userMenu={<ProfileMenu />}
 * />
 */
export const ProfileMenu: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useUser();

  /**
   * Handle navigation to profile edit page
   */
  const handleEditProfile = (): void => {
    navigate('/profile/edit');
  };

  /**
   * Handle sign out navigation
   */
  const handleSignOut = (): void => {
    navigate('/signout');
  };

  // Don't render if no user
  if (!user) {
    return null;
  }

  const initials = getUserInitials(user);
  const displayName = getUserDisplayName(user);

  return (
    <Menu>
      <MenuButton
        as={Box}
        cursor="pointer"
        _hover={{ opacity: 0.8 }}
        transition="opacity 0.2s"
      >
        <HStack spacing={2}>
          <Avatar
            size="sm"
            name={displayName}
            bg="xacto.electricCyan"
            color="xacto.deepNavy"
            fontWeight="bold"
          >
            {initials}
          </Avatar>
          <ChevronDownIcon display={{ base: 'none', md: 'block' }} />
        </HStack>
      </MenuButton>

      <MenuList
        bg="bg.surface"
        borderColor="xacto.mutedNavy"
        py={2}
      >
        {/* User info header */}
        <Box px={4} py={2} borderBottomWidth="1px" borderColor="xacto.mutedNavy">
          <VStack align="start" spacing={0}>
            <Text fontWeight="semibold" color="text.primary" fontSize="sm">
              {displayName}
            </Text>
            <Text color="text.secondary" fontSize="xs">
              {user.email}
            </Text>
          </VStack>
        </Box>

        {/* Edit Profile */}
        <MenuItem
          icon={<Icon as={MdPerson} boxSize={18} />}
          onClick={handleEditProfile}
          bg="transparent"
          _hover={{ bg: 'bg.muted' }}
          color="text.secondary"
          mt={2}
        >
          Edit Profile
        </MenuItem>

        <MenuDivider borderColor="xacto.mutedNavy" />

        {/* Sign Out */}
        <MenuItem
          icon={<Icon as={MdLogout} boxSize={18} />}
          onClick={handleSignOut}
          bg="transparent"
          _hover={{ bg: 'bg.muted' }}
          color="red.400"
        >
          Sign Out
        </MenuItem>
      </MenuList>
    </Menu>
  );
};

export default ProfileMenu;
